/** 逐条同步 head 里的 SEO 标签。SPA 没有服务端渲染，只能在这里把文档头改对。 */
import { useEffect } from 'react';
import { SITE } from '@/content/site';
import { absoluteUrl, jsonLdScript } from '@/lib/seo';

const LD_ID = 'havorix-jsonld';
const OG_IMAGE = '/og-image.svg';

export interface DocumentMeta {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article';
  publishedAt?: string;
  jsonLd?: readonly object[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, type?: string) {
  const selector = type ? `link[rel="${rel}"][type="${type}"]` : `link[rel="${rel}"]:not([type])`;
  let tag = document.head.querySelector<HTMLLinkElement>(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    if (type) tag.setAttribute('type', type);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/** 当前页对应的 Markdown 地址；首页是 /index.md，其余是「路径 + .md」。 */
function markdownPath(path: string): string {
  const trimmed = path === '/' ? '/' : path.replace(/\/+$/, '');
  return trimmed === '/' ? '/index.md' : `${trimmed}.md`;
}

export function useDocumentMeta({ title, description, path, type = 'website', publishedAt, jsonLd }: DocumentMeta) {
  const serialised = jsonLd ? jsonLdScript(jsonLd) : null;

  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', absoluteUrl(path));
    upsertMeta('property', 'og:site_name', 'Havorix');
    upsertMeta('property', 'og:locale', SITE.locale.replace('-', '_'));
    upsertMeta('property', 'og:image', absoluteUrl(OG_IMAGE));
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', absoluteUrl(OG_IMAGE));
    if (publishedAt) upsertMeta('property', 'article:published_time', publishedAt);
    upsertLink('canonical', absoluteUrl(path));
    // 同一内容的纯文本表示，供解析 HTML 的 Agent 直接取用。
    upsertLink('alternate', absoluteUrl(markdownPath(path)), 'text/markdown');

    if (serialised) {
      let script = document.getElementById(LD_ID);
      if (!script) {
        script = document.createElement('script');
        script.id = LD_ID;
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = serialised;
    } else {
      document.getElementById(LD_ID)?.remove();
    }
  }, [title, description, path, type, publishedAt, serialised]);
}
